import { motion } from "framer-motion";
import { Fuel, ShieldCheck, Users, Wrench, HeartPulse, Handshake, Clock } from "lucide-react";
import { IMAGES } from "@/lib/assets";
import { useSettings } from "@/lib/SettingsContext";

const ICONS = {
  fuel: Fuel,
  insurance: ShieldCheck,
  roadside: Wrench,
  community: Users,
  health: HeartPulse,
  partners: Handshake,
};

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 },
  }),
};

const ComingSoon = () => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-400"
    data-testid="coming-soon-badge"
  >
    <Clock size={11} /> Coming Soon
  </span>
);

const spanClass = {
  large: "md:col-span-2 md:row-span-2",
  tall: "md:row-span-2",
  small: "",
};

export const Benefits = () => {
  const CONTENT = useSettings();
  const { chapter, heading, items } = CONTENT.benefits;

  return (
    <section id="benefits" className="relative py-28 md:py-36 bg-[#eef3f9]" data-testid="benefits">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl mb-16">
          <motion.p
            variants={reveal} custom={0} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="text-xs tracking-[0.3em] uppercase text-amber-500 font-bold mb-5"
          >
            {chapter}
          </motion.p>
          <motion.h2
            variants={reveal} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="font-display font-extrabold uppercase tracking-tight text-4xl md:text-5xl leading-[0.95] text-navy-dark"
          >
            {heading}
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:auto-rows-[220px]">
          {items.filter((it) => !it.hidden).map((item, i) => {
            const Icon = ICONS[item.icon] || ShieldCheck;
            const isLarge = item.size === "large";
            const isTall = item.size === "tall";
            const imgBg = isLarge ? IMAGES.aerialHighway : isTall ? IMAGES.driverPortrait : null;

            return (
              <motion.div
                key={item.title}
                variants={reveal} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}
                className={`group relative overflow-hidden rounded-md border border-slate-200 bg-white hover:border-amber-500/50 hover:shadow-lg hover:shadow-slate-200/70 transition-all ${spanClass[item.size] || ""}`}
                data-testid={`benefit-${item.icon}`}
              >
                {imgBg && (
                  <>
                    <img
                      src={imgBg}
                      alt=""
                      aria-hidden
                      className={`absolute inset-0 w-full h-full object-cover ${isLarge ? "opacity-40 group-hover:opacity-55" : "opacity-50 group-hover:opacity-65"} group-hover:scale-105 transition-all duration-700`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#08122a] via-[#08122a]/85 to-[#08122a]/40" />
                  </>
                )}

                <div className={`relative h-full flex flex-col ${isLarge || isTall ? "justify-end" : "justify-between"} p-7 md:p-8`}>
                  <div className="flex items-start justify-between">
                    <Icon className="text-amber-500 mb-3" size={isLarge ? 34 : 30} />
                    {item.comingSoon && <ComingSoon />}
                  </div>

                  {isLarge && item.stat && (
                    <div className="font-display font-black text-5xl md:text-6xl text-white leading-none mt-2">
                      {item.stat}
                      {item.statUnit && <span className="text-amber-500">{item.statUnit}</span>}
                    </div>
                  )}

                  <div className={isLarge ? "mt-3" : ""}>
                    <h3 className={`font-bold ${imgBg ? "text-white" : "text-navy-dark"} ${isLarge || isTall ? "text-xl" : "text-lg"}`}>{item.title}</h3>
                    <p className={`mt-2 ${imgBg ? "text-slate-100" : "text-zinc-400"} ${isLarge ? "text-base max-w-md" : "text-sm"}`}>
                      {item.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
